/**
 * Voucher pembayaran gabungan: logika murni untuk 2-5 invoice satu supplier.
 *
 * Tanpa React, tanpa DOM, tanpa window — supaya setiap guard dan setiap
 * aritmetika bisa diuji tanpa merender apa pun.
 *
 * Guard di sini adalah cermin dari guard server (backend/voucher_gabungan.py),
 * BUKAN penggantinya. Tugasnya memberi umpan balik cepat dan pesan yang jelas;
 * keputusan yang mengikat tetap di server, karena gate sisi klien hanya
 * kondisi render dan bisa dilewati dengan satu curl.
 */
import { invoiceAmounts, isSettled } from './invoiceAmounts';

/**
 * Batas 5 menjaga tiga hal sekaligus: panjang header "No. Voucher" (5 nomor
 * ≈ 75 karakter, masih aman untuk .top-info yang flex tanpa min-width:0),
 * keterbacaan, dan page-break — VOUCHER_STYLE tidak punya aturan
 * page-break-inside dan tidak ada <thead> berulang, jadi semakin sedikit blok
 * semakin kecil peluang blok tanda tangan terlempar ke halaman sendiri.
 */
export const MIN_BATCH = 2;
export const MAX_BATCH = 5;

const num = (v) => Number(v) || 0;

/**
 * Trim saja — sengaja TIDAK membuang tanda hubung atau nol di depan.
 * supplier_data tiap invoice adalah snapshot independen dan export bank
 * membayar dari snapshot itu, jadi "123-456" dan "123456" memang dua string
 * rekening yang berbeda. Menormalkannya akan menggabungkan tujuan yang
 * sungguh-sungguh berlainan.
 */
const t = (v) => (v === null || v === undefined ? '' : String(v).trim());

/**
 * Pembulatan yang meniru `rupiah()` di voucherTemplate: toLocaleString('id-ID')
 * tanpa opsi memakai maximumFractionDigits default 3, dan half-expand.
 * Math.round sendiri berbeda dari Intl pada tie negatif, karena itu
 * magnitudonya dibulatkan lalu tandanya dipasang kembali.
 */
export const displayValue = (v) => {
  const n = num(v);
  return Math.sign(n) * (Math.round(Math.abs(n) * 1000) / 1000);
};

/**
 * Identitas rekening tujuan — WAJIB memuat override Virtual Account.
 *
 * Tujuan sebenarnya bukan selalu supplier_data: backend memakai va_nomor bila
 * uses_va. Dua invoice dengan supplier, perusahaan, dan supplier_data identik
 * tapi uses_va berbeda akan dibayar ke dua rekening berbeda; tanpa VA di kunci
 * ini guard akan meloloskannya dan voucher menyatakan satu tujuan untuk uang
 * yang pergi ke dua tempat.
 */
export const destinationKey = (inv) => [
  inv?.uses_va ? '1' : '0',
  t(inv?.va_nomor),
  t(inv?.va_nama),
  t(inv?.supplier_data?.nama_bank),
  t(inv?.supplier_data?.nomor_rekening),
].join('|');

/** Tujuan dalam bentuk terbaca, untuk pesan error dan header voucher. */
export const destinationLabel = (inv) => {
  const bank = t(inv?.supplier_data?.nama_bank) || '-';
  if (inv?.uses_va) {
    return `${bank} - ${t(inv?.va_nomor) || '-'} (VIRTUAL ACCOUNT)`;
  }
  return `${bank} - ${t(inv?.supplier_data?.nomor_rekening) || '-'}`;
};

const nomor = (inv) => t(inv?.invoice_number) || t(inv?.id);

/**
 * Urutan tampil: tanggal, lalu nomor, lalu id agar deterministik penuh.
 *
 * Pemisahnya U+0000, bukan spasi: karakter itu tidak mungkin muncul di data,
 * jadi batas antar-field tidak bisa rancu. Dengan spasi, nomor "INV 2" + id
 * "x" dan nomor "INV" + id "2 x" menghasilkan kunci yang sama persis.
 *
 * Ditulis sebagai escape '\u0000', JANGAN byte NUL literal: byte literal
 * membuat git menandai berkas ini binary sehingga diff-nya hilang selamanya.
 */
export const orderMembers = (invoices) =>
  [...(invoices || [])].sort((a, b) => {
    const ka = [t(a.invoice_date), nomor(a), t(a.id)].join('\u0000');
    const kb = [t(b.invoice_date), nomor(b), t(b.id)].join('\u0000');
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });

/**
 * Semua guard. Mengembalikan { ok, errors, warnings } — errors mengumpulkan
 * SEMUA kegagalan, bukan yang pertama, supaya user bisa membereskannya sekali
 * jalan.
 *
 * Urutannya disengaja: keberadaan data diperiksa SEBELUM kesetaraan. Tanpa itu
 * dua invoice dari supplier BERBEDA yang keduanya `supplier_id: null` akan
 * lolos uji "satu supplier", karena null === null.
 */
export function validateVoucherBatch(invoices) {
  const members = invoices || [];
  const errors = [];
  const warnings = [];
  const push = (code, message) => errors.push({ code, message });

  if (members.length < MIN_BATCH) {
    push('BATCH_SINGLE',
      `Pilih minimal ${MIN_BATCH} invoice. Untuk satu invoice, gunakan tombol cetak di barisnya.`);
    return { ok: false, errors, warnings };
  }
  if (members.length > MAX_BATCH) {
    push('BATCH_TOO_LARGE',
      `Maksimal ${MAX_BATCH} invoice per voucher gabungan (terpilih ${members.length}).`);
  }

  const ids = members.map((m) => t(m.id));
  if (new Set(ids).size !== ids.length) {
    push('DUPLICATE_ID', 'Ada invoice yang dipilih lebih dari sekali.');
  }

  // --- kelayakan per invoice ---
  const salahStatus = members.filter((m) => m.status !== 'proses_bayar');
  if (salahStatus.length) {
    push('STATUS_BUKAN_PROSES_BAYAR',
      `Invoice berikut bukan berstatus Proses Bayar: ${salahStatus.map((m) => `${nomor(m)} (${m.status})`).join(', ')}.`);
  }

  const beku = members.filter((m) => isSettled(m));
  if (beku.length) {
    push('SETTLED_MEMBER',
      `Invoice berikut sudah ditandai terbayar sehingga nominalnya beku dan tidak bisa digabung: ${beku.map(nomor).join(', ')}. Cetak terpisah.`);
  }

  const tanpaVoucher = members.filter((m) => !t(m.voucher_number));
  if (tanpaVoucher.length) {
    push('MISSING_VOUCHER_NUMBER',
      `Invoice berikut belum punya nomor voucher: ${tanpaVoucher.map(nomor).join(', ')}. Submit ke pembayaran terlebih dahulu.`);
  }

  // --- satu supplier: bandingkan id, bukan nama (dua supplier bisa senama) ---
  const tanpaSupplier = members.filter((m) => !t(m.supplier_id));
  if (tanpaSupplier.length) {
    push('MISSING_SUPPLIER_ID',
      `Invoice berikut tidak punya data supplier yang valid: ${tanpaSupplier.map(nomor).join(', ')}.`);
  } else if (new Set(members.map((m) => t(m.supplier_id))).size !== 1) {
    const nama = [...new Set(members.map((m) => t(m.supplier_name) || '(tanpa nama)'))].sort();
    push('SUPPLIER_MISMATCH', `Semua invoice harus dari supplier yang sama. Terpilih: ${nama.join(', ')}.`);
  }

  // --- satu perusahaan: pembebanan & penanda tangan voucher mengikutinya ---
  const tanpaPerusahaan = members.filter((m) => !t(m.company_id));
  if (tanpaPerusahaan.length) {
    push('MISSING_COMPANY_ID',
      `Invoice berikut tidak punya data perusahaan: ${tanpaPerusahaan.map(nomor).join(', ')}.`);
  } else if (new Set(members.map((m) => t(m.company_id))).size !== 1) {
    const nama = [...new Set(members.map((m) => t(m.company_name) || '(tanpa nama)'))].sort();
    push('COMPANY_MISMATCH',
      `Semua invoice harus dari perusahaan yang sama, karena pembebanan dan penanda tangan voucher mengikuti perusahaan. Terpilih: ${nama.join(', ')}.`);
  }

  // --- satu tujuan ---
  const vaTanpaNomor = members.filter((m) => m.uses_va && !t(m.va_nomor));
  if (vaTanpaNomor.length) {
    push('VA_INCOMPLETE',
      `Invoice berikut memakai Virtual Account tapi nomor VA-nya kosong: ${vaTanpaNomor.map(nomor).join(', ')}.`);
  }

  const tanpaRekening = members.filter((m) => !m.uses_va && !t(m.supplier_data?.nomor_rekening));
  if (tanpaRekening.length) {
    push('MISSING_REKENING',
      `Invoice berikut tidak punya nomor rekening tujuan: ${tanpaRekening.map(nomor).join(', ')}.`);
  }

  if (!vaTanpaNomor.length && !tanpaRekening.length) {
    if (new Set(members.map(destinationKey)).size !== 1) {
      const rincian = members.map((m) => `${nomor(m)} -> ${destinationLabel(m)}`).join(', ');
      push('DESTINATION_MISMATCH',
        `Satu voucher hanya boleh punya satu rekening tujuan. Rekening/VA yang terpilih berbeda: ${rincian}.`);
    }
  }

  // --- sudah tergabung di grup lain ---
  const tergabung = members.filter((m) => t(m.voucher_gabungan_id));
  if (tergabung.length) {
    push('ALREADY_GROUPED',
      `${tergabung.length} invoice sudah tergabung dalam voucher gabungan lain: ${tergabung
        .map((m) => `${nomor(m)} (${t(m.voucher_gabungan_label) || t(m.voucher_gabungan_id)})`)
        .join(', ')}. Lepas dari gabungan terlebih dahulu.`);
  }

  // --- peringatan, bukan penghalang ---
  const pengurang = members.filter((m) => invoiceAmounts(m).nilaiDibayarkan <= 0);
  if (pengurang.length) {
    warnings.push({
      code: 'NON_POSITIVE',
      message: `Invoice berikut bernilai nol atau negatif sehingga MENGURANGI total gabungan: ${pengurang
        .map(nomor)
        .join(', ')}. Barisnya akan ditandai pada voucher.`,
    });
  }

  return { ok: errors.length === 0, errors, warnings };
}

/** Satu baris per (jenis PPh, tarif) — jangan pernah satu baris agregat. */
export function groupPphByKey(invoices) {
  const peta = new Map();
  for (const inv of invoices || []) {
    const a = invoiceAmounts(inv);
    if (!a.pphType) continue;
    const key = `${a.pphType}|${a.pphPercentage}`;
    const prev = peta.get(key) || { label: `${a.pphType} (${a.pphPercentage}%)`, amount: 0 };
    prev.amount += a.pphAmount;
    peta.set(key, prev);
  }
  return [...peta.values()].sort((x, y) => (x.label < y.label ? -1 : 1));
}

/**
 * Satu baris per tarif PPn.
 *
 * `percentage` ikut dikembalikan supaya template bisa menyusun label bergaya
 * voucher satuan ("PPn Masukan: [v] Ada PPn (11%)") tanpa mem-parse `label`.
 */
export function groupPpnByRate(invoices) {
  const peta = new Map();
  for (const inv of invoices || []) {
    const a = invoiceAmounts(inv);
    if (!a.hasPpn) continue;
    const key = String(a.ppnPercentage);
    const prev = peta.get(key)
      || { label: `PPn ${a.ppnPercentage}%`, percentage: a.ppnPercentage, amount: 0 };
    prev.amount += a.ppnAmount;
    peta.set(key, prev);
  }
  return [...peta.values()].sort((x, y) => (x.label < y.label ? -1 : 1));
}

/**
 * Selisih antara total EKSAK dan jumlah penjumlah yang BENAR-BENAR TERCETAK.
 *
 * Dihitung dari nilai tercetak, bukan nilai eksak, karena yang direkonsiliasi
 * pembaca adalah kertasnya: `rupiah()` membulatkan ke 3 desimal sementara
 * nominal bisa 4 desimal dan PPn/PPh tidak dibulatkan sama sekali.
 */
const residual = (exact, addends) =>
  displayValue(displayValue(exact) - addends.reduce((s, v) => s + v, 0));

/**
 * Seluruh angka voucher gabungan — satu rantai agregat, bukan blok per invoice.
 *
 * `totalDibayarkan` tetap EKSAK (Σ nilaiDibayarkan) karena ia harus sama
 * dengan yang dipindahkan Proses Bank. Yang dibulatkan hanya tampilannya.
 *
 * Empat medan `penyesuaian*` menutup selisih antara baris yang TERCETAK dan
 * total yang tercetak. Masing-masing menjaga satu klaim penjumlahan yang bisa
 * diperiksa pembaca, dan dirender HANYA bila ≠ 0 — pada angka rupiah nyata
 * keempatnya selalu nol, jadi voucher normal tidak menampilkan satu pun.
 * Pada dokumen keuangan, kolom yang tidak bisa dijumlahkan pembaca adalah
 * cacat, bukan detail.
 *
 * Kondisi tampil tiap baris opsional ikut diperhitungkan di sini (lihat
 * `basisTercetak`): residu dihitung terhadap rantai yang benar-benar muncul,
 * bukan terhadap rantai ideal.
 *
 * Galat float bukan masalahnya: untuk 5 suku bermagnitudo ≲1e10 galatnya
 * ≈1e-5, tiga orde di bawah ambang tampilan 5e-4. Jangan beralih ke integer
 * sen — itu justru membuat total voucher menyimpang dari total_amount yang
 * dipakai export bank.
 */
export function batchTotals(invoices) {
  const ordered = orderMembers(invoices);
  const rows = ordered.map((inv) => ({ inv, a: invoiceAmounts(inv) }));
  const sum = (pick) => rows.reduce((s, r) => s + num(pick(r)), 0);
  const d = displayValue;

  const subtotal = sum((r) => r.a.subtotal);
  const potonganRetur = sum((r) => r.a.potonganRetur);
  const dpp = sum((r) => r.a.dpp);
  const biayaAngkut = sum((r) => r.a.biayaAngkut);
  const pembulatan = sum((r) => r.a.pembulatan);
  const dpAmount = sum((r) => r.a.dpAmount);
  const totalTagihan = sum((r) => r.a.totalTagihan);
  const adminBank = sum((r) => r.a.adminBank);
  const totalDibayarkan = sum((r) => r.a.nilaiDibayarkan);

  const pphByKey = groupPphByKey(ordered);
  const ppnByRate = groupPpnByRate(ordered);

  // Nilai seperti yang tercetak, mengikuti kondisi tampil tiap baris. Baris
  // yang disembunyikan menyumbang 0 — sama seperti di kertas.
  const ppnTercetak = ppnByRate.reduce((s, x) => s + d(x.amount), 0);
  const pphTercetak = pphByKey.reduce((s, x) => s + d(x.amount), 0);
  const angkutTercetak = biayaAngkut > 0 ? d(biayaAngkut) : 0;
  const pembulatanTercetak = pembulatan !== 0 ? d(pembulatan) : 0;
  const dpTercetak = dpAmount > 0 ? d(dpAmount) : 0;
  const adminTercetak = adminBank > 0 ? d(adminBank) : 0;

  // Tanpa potongan/retur, baris DPP tidak dicetak sama sekali dan subtotal-lah
  // yang jadi titik tolak rantai — persis seperti voucher satuan.
  const adaRetur = potonganRetur > 0;
  const basisTercetak = adaRetur ? d(dpp) : d(subtotal);

  return {
    rows,
    subtotal,
    potonganRetur,
    dpp,
    biayaAngkut,
    pembulatan,
    dpAmount,
    totalTagihan,
    adminBank,
    pphByKey,
    ppnByRate,
    totalDibayarkan,

    // Baris DPP vs Subtotal − Potongan/Retur.
    penyesuaianDpp: adaRetur ? residual(dpp, [d(subtotal), -d(potonganRetur)]) : 0,
    // Total Tagihan vs seluruh penjumlah di atasnya.
    penyesuaianTagihan: residual(totalTagihan, [
      basisTercetak, ppnTercetak, angkutTercetak, pembulatanTercetak, -dpTercetak,
    ]),
    // Total Dibayarkan vs Total Tagihan − PPh − Admin Bank.
    penyesuaianBayar: residual(totalDibayarkan, [
      d(totalTagihan), -pphTercetak, -adminTercetak,
    ]),
    // Total Dibayarkan vs Σ rincian per invoice yang tercetak di bawahnya.
    penyesuaianRincian: residual(
      totalDibayarkan,
      rows.map((r) => d(r.a.nilaiDibayarkan)),
    ),
  };
}
